import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  Bars3Icon, 
  WalletIcon, 
  ArrowRightOnRectangleIcon,
  BuildingOfficeIcon,
  GlobeAsiaAustraliaIcon,
  BuildingStorefrontIcon,
  StarIcon,
  UserGroupIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import TopNav from '../TopNav';
import WalletModal from '../WalletModal';
import AddUserButton from '../AddUserButton';
import { getUsersData, buildTree } from '../../data/users';
import useTranslation from '../../services/translationService';
import { isUserRestricted } from '../../services/breakglassService';

// Track if we've already fetched the user type to avoid redundant calls
let userTypeInitialized = false;

// User type definitions
const USER_TYPES = {
  ADMIN: 'admin',
  COUNTRY_ADMIN: 'country',
  CITY_ADMIN: 'city',
  SUPER_AGENT: 'super-agent',
  AGENT: 'agent',
  PLAYER: 'player'
};

// Icon mapping based on user type
const USER_TYPE_ICONS = {
  [USER_TYPES.ADMIN]: BuildingOfficeIcon,
  [USER_TYPES.COUNTRY_ADMIN]: GlobeAsiaAustraliaIcon,
  [USER_TYPES.CITY_ADMIN]: BuildingStorefrontIcon,
  [USER_TYPES.SUPER_AGENT]: StarIcon,
  [USER_TYPES.AGENT]: UserGroupIcon,
  [USER_TYPES.PLAYER]: UserIcon
};

// Color classes for user type badges
const USER_TYPE_COLORS = {
  [USER_TYPES.ADMIN]: {
    bg: 'bg-purple-50 dark:bg-purple-500/15',
    text: 'text-purple-700 dark:text-purple-400',
    icon: 'text-purple-500 dark:text-purple-400'
  },
  [USER_TYPES.COUNTRY_ADMIN]: {
    bg: 'bg-blue-50 dark:bg-blue-500/15',
    text: 'text-blue-700 dark:text-blue-400',
    icon: 'text-blue-500 dark:text-blue-400'
  },
  [USER_TYPES.CITY_ADMIN]: {
    bg: 'bg-green-50 dark:bg-green-500/15',
    text: 'text-green-700 dark:text-green-400',
    icon: 'text-green-500 dark:text-green-400'
  },
  [USER_TYPES.SUPER_AGENT]: {
    bg: 'bg-orange-50 dark:bg-orange-500/15',
    text: 'text-orange-700 dark:text-orange-400',
    icon: 'text-orange-500 dark:text-orange-400'
  },
  [USER_TYPES.AGENT]: {
    bg: 'bg-yellow-50 dark:bg-yellow-500/15',
    text: 'text-yellow-700 dark:text-yellow-400',
    icon: 'text-yellow-500 dark:text-yellow-400'
  },
  [USER_TYPES.PLAYER]: {
    bg: 'bg-gray-100 dark:bg-white/10',
    text: 'text-gray-600 dark:text-gray-400',
    icon: 'text-gray-500 dark:text-gray-400'
  }
};

const Layout = ({ children, user, onSignOut }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isWalletOpen, setIsWalletOpen] = useState(false);
  const [userType, setUserType] = useState(null);
  const [userTree, setUserTree] = useState(null);
  const [isRestricted, setIsRestricted] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();

  // Check if user is restricted
  useEffect(() => {
    const checkRestriction = async () => {
      const restricted = await isUserRestricted();
      setIsRestricted(restricted);
    };
    checkRestriction();
  }, []);

  // Fetch user type and tree data
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        // Skip if we already fetched the user type
        if (userTypeInitialized && userType) {
          console.log("🧑 Using cached user type:", userType);
          return;
        }

        console.log("🧑 Fetching user data...");
        const response = await getUsersData(false); // Use cached data, don't force refresh
        if (response.success) {
          const tree = await buildTree(response.data, response.data.rootId);
          setUserTree(tree);
          
          // Find the current user in the tree
          const findUser = (node) => {
            if (node.username === user.username) {
              return node.type;
            }
            if (node.children) {
              for (const child of node.children) {
                const found = findUser(child);
                if (found) return found;
              }
            }
            return null;
          };
          const type = findUser(tree);
          setUserType(type);
          userTypeInitialized = true;
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    if (user && user.username) {
      fetchUserData();
    }
  }, [user, userType]);

  console.log('Layout user:', user); // Debug log
  console.log('Layout userType:', userType); // Debug log

  // Get page title based on current route
  const getPageTitle = () => {
    const path = location.pathname.split('/')[1];
    if (!path) return t('navigation.dashboard');
    
    // Map path to translation key
    const pathToKey = {
      'dashboard': 'navigation.dashboard',
      'agent-tree': 'navigation.agentTree',
      'transactions': 'navigation.transactions',
      'users': 'navigation.users',
      'settings': 'navigation.settings',
      'notifications': 'navigation.notifications',
      'reports': 'navigation.reports',
      'admin-analytics': 'navigation.adminAnalytics',
      'admin-statistics': 'Admin Statistics'
    };
    
    return pathToKey[path] ? (pathToKey[path].includes('navigation.') ? t(pathToKey[path]) : pathToKey[path]) : path.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const IdentityIcon = USER_TYPE_ICONS[userType] || UserIcon;
  const identityColors = USER_TYPE_COLORS[userType] || USER_TYPE_COLORS[USER_TYPES.PLAYER];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
      <header className="fixed top-0 left-0 right-0 z-[40] pt-[env(safe-area-inset-top)]">
        {/* Translucent surface lives on its own layer: backdrop-filter on the header itself would
            become the containing block for fixed-position elements rendered inside it */}
        <div
          className="absolute inset-0 -z-10 border-b border-gray-200/70 bg-white/90 backdrop-blur-md dark:border-white/10 dark:bg-gray-900/85"
          aria-hidden="true"
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="flex items-center justify-between gap-3 h-14">
            {/* Left side - Menu and Title */}
            <div className="flex flex-shrink-0 items-center gap-2">
              <button
                onClick={() => setIsMenuOpen(true)}
                className="-ms-2 flex h-10 w-10 items-center justify-center rounded-full bg-transparent text-gray-600
                  hover:bg-gray-100 active:bg-gray-200/70 dark:bg-transparent dark:text-gray-300 dark:hover:bg-white/10
                  transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                aria-label={t('navigation.menu')}
              >
                <Bars3Icon className="w-6 h-6" />
              </button>
              <h1 className="text-base font-semibold text-gray-900 dark:text-white hidden sm:block">
                {getPageTitle()}
              </h1>
            </div>
            
            {/* Right side - User controls */}
            <div className="flex min-w-0 items-center gap-3">
              {/* User display with type icon */}
              <div className="flex min-w-0 items-center gap-2">
                <span className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${identityColors.bg}`}>
                  <IdentityIcon className={`w-4 h-4 ${identityColors.icon}`} />
                </span>
                <div className="min-w-0 leading-tight">
                  <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">
                    {user.username}
                  </p>
                  {userType && (
                    <p className={`truncate text-[11px] font-medium ${identityColors.text}`}>
                      {t(`userTypes.${userType}`)}
                    </p>
                  )}
                </div>
              </div>
              {/* Hide wallet and add user buttons for restricted users */}
              {!isRestricted && (
                <div className="flex flex-shrink-0 items-center gap-1.5">
                  <button
                    onClick={() => setIsWalletOpen(true)}
                    className="flex h-10 w-10 items-center justify-center rounded-full bg-teal-50 text-teal-600
                      hover:bg-teal-100 active:bg-teal-200/70 dark:bg-teal-500/10 dark:text-teal-300 dark:hover:bg-teal-500/20
                      transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
                    aria-label={t('wallet.title')}
                  >
                    <WalletIcon className="w-5 h-5" />
                  </button>
                  <AddUserButton userType={userType} />
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Slide-over menu */}
      <TopNav 
        isOpen={isMenuOpen} 
        onClose={() => setIsMenuOpen(false)} 
        onSignOut={onSignOut}
        userType={userType}
        username={user?.username}
      />

      {/* Wallet modal */}
      <WalletModal 
        isOpen={isWalletOpen} 
        onClose={() => setIsWalletOpen(false)} 
        user={user}
        userTree={userTree}
      />

      <main className="relative max-w-7xl mx-auto px-2 sm:px-4 py-6 mt-header">
        {children}
      </main>
    </div>
  );
};

export default Layout; 